import React, { useEffect, useMemo, useState } from 'react';
import {
  TARA_MODULE_KEYS,
} from '../utils/classifyTaraPaperModule';
import {
  BMAT_MODULE_KEYS,
  BMAT_MODULE_LABELS,
} from '../utils/classifyBmatPaperSection';
import {
  formatPaperRaw,
  formatTaraScore,
  loadTaraPaperScores,
  loadTaraTarget,
  resetTaraSittingProgress,
  saveTaraPaperScore,
  saveTaraTarget,
  sittingScoreFromTaraModules,
  taraScoreFromRaw,
  taraSittingHubStatus,
  taraStatusFromDbPapers,
} from '../utils/taraScore';
import useWorkspacePaperProgress, { useUserTarget } from '../hooks/useWorkspacePaperProgress';
import { taraWritingFeedbackAccess } from '../utils/taraWritingFeedbackAccess';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { collectTopicPaperIds, topicAttemptRowFromMap, topicAttemptStatusRequestKey, topicRowAction } from '../utils/topicAttemptRow';
import { firstPopulatedMockTier } from '../utils/firstPopulatedMockTier';
import { useAuth } from '../../auth/hooks/useAuth';
import PracticeTopicsNav from './PracticeTopicsNav';
import PracticeTopicsPages from './PracticeTopicsPages';
import { topicPapersContent } from './TopicPapersBlock';
import {
  SUBTOPIC_PAPERS_LABEL,
  SUBTOPIC_PAPERS_ALL_PAGE,
  SUBTOPIC_TIER_PAGES,
  SUBTOPIC_PAGE_TITLES,
  subtopicPapersOfTier,
} from '../utils/subtopicPapers';
import PracticeTopicsOverview from './PracticeTopicsOverview';
import useSectionPaperTierProgress from '../hooks/useSectionPaperTierProgress';
import { PRACTICE_TOPIC_PAGE_TITLES, practiceProgressSummary } from '../utils/practiceTopics';
import TaraWritingFeedbackPanel from './TaraWritingFeedbackPanel';
import './TaraWorkspace.css';

const PAGES = {
  overview: 'Overview',
  ...PRACTICE_TOPIC_PAGE_TITLES,
  'mock-all': 'Mock Tests',
  'mock-beginnings': 'Mock Tests: Beginnings',
  'mock-foundations': 'Mock Tests: Foundations',
  'mock-beyond': 'Mock Tests: Beyond',
  'past-bmat': 'Past Papers: BMAT',
  'past-tsa': 'Past Papers: TSA',
  'past-tara': 'Past Papers: TARA',
  'writing-feedback': 'Writing Task Feedback',
};

const TARGET_OPTIONS = [
  { value: 68, requirement: 'Mandatory', label: 'University of Oxford — PPE / Economics & Management' },
  { value: 65, requirement: 'Mandatory', label: 'University of Cambridge — Human, Social & Political Sciences' },
  { value: 64, requirement: 'Mandatory', label: 'London School of Economics (LSE) — BSc Politics & Economics' },
  { value: 65, requirement: 'Mandatory', label: 'Imperial College London — BSc Business & Technology' },
  { value: 62, requirement: 'Mandatory', label: "King's College London (KCL) — BSc Political Economy" },
  { value: 60, requirement: 'Recommended', label: 'Durham University — BA Philosophy, Politics & Economics' },
  { value: 62, requirement: 'Recommended', label: 'University of Warwick — BA Economics & Global Affairs' },
  { value: 60, requirement: 'Recommended', label: 'University of Bristol — BSc Politics & International Relations' },
  { value: 58, requirement: 'Recommended', label: 'SOAS University of London — BA International Development' },
  { value: 60, requirement: 'Recommended', label: 'University of St Andrews — MA Sustainable Development' },
];

function sittingPaperIds(sitting) {
  if (sitting?.tsaSitting) {
    return [sitting.paper1?.id, sitting.paper2?.id].filter(Boolean);
  }
  if (sitting?.bmatSitting) {
    return BMAT_MODULE_KEYS.map((key) => sitting?.bmatModules?.[key]?.id).filter(Boolean);
  }
  return TARA_MODULE_KEYS.map((key) => sitting?.taraModules?.[key]?.id).filter(Boolean);
}

function sittingLabel(sitting, index, kind) {
  if (kind === 'past') {
    const year = String(sitting?.title || '').match(/\b((?:19|20)\d{2})\b/);
    return year ? year[1] : sitting?.title || `Paper ${index + 1}`;
  }
  return `Test ${index + 1}`;
}

function paperHeritagePrefix(item) {
  const hay = [item?.id, item?.catalog_id, item?.startId, item?.display_name, item?.title, item?.heritageExam]
    .map((v) => String(v || '').toUpperCase())
    .join(' ');
  if (hay.includes('BMAT')) return 'BMAT';
  if (hay.includes('TSA') || item?.tsaSitting) return 'TSA';
  if (hay.includes('TARA')) return 'TARA';
  return null;
}

/** Specimen (1001) + Sample (1002) only — exclude Foundations/Beyond mocks. */
function isTaraSpecimenOrSample(item) {
  const hay = [
    item?.id,
    item?.catalog_id,
    item?.startId,
    item?.title,
    item?.display_name,
    item?.taraModules?.critical_thinking?.id,
  ].map((v) => String(v || '').toLowerCase()).join(' ');
  if (/tara-100[12]\b/.test(hay)) return true;
  if (/\b(specimen|sample)\b/.test(hay) && !/\bmock\b/.test(hay)) return true;
  return false;
}

function creamPaperLabel(paper) {
  const hay = `${paper?.display_name || ''} ${paper?.title || ''} ${paper?.id || ''}`;
  const year = hay.match(/\b((?:19|20)\d{2})\b/);
  if (paperHeritagePrefix(paper) === 'BMAT' && year) return year[1];
  const title = String(paper?.display_name || paper?.title || '').trim();
  if (title) return title;
  return String(paper?.id || paper?.catalog_id || 'Paper');
}

function enrichCreamPapers(papers, scoresMap, dbPapers = {}) {
  return (papers || [])
    .filter((p) => p && p.kind !== 'sitting')
    .map((paper, index) => {
      const id = paper.id;
      const dbStatus = taraStatusFromDbPapers([id], dbPapers);
      const s = id ? scoresMap[String(id)] : null;
      const score = s && s.total > 0 ? taraScoreFromRaw(s.correct, s.total) : null;
      let status = dbStatus;
      if (status === 'none' && s && s.total > 0) status = 'done';
      return {
        ...paper,
        index,
        scored: {
          correct: s?.correct,
          total: s?.total,
          score,
          mode: s?.mode || null,
        },
        status,
      };
    })
    .sort((a, b) => String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true }));
}

function enrichTsaSittings(sittings, scoresMap, dbPapers = {}) {
  return (sittings || [])
    .filter((s) => s?.kind === 'sitting' && s.tsaSitting)
    .map((sitting, index) => {
      const ids = [sitting.paper1?.id, sitting.paper2?.id].filter(Boolean);
      const s1 = sitting.paper1?.id ? scoresMap[String(sitting.paper1.id)] : null;
      const s2 = sitting.paper2?.id ? scoresMap[String(sitting.paper2.id)] : null;
      const s1Done = Boolean(s1 && s1.total > 0);
      const s2Done = Boolean(s2 && (s2.total > 0 || s2.dbStatus === 'completed'));
      let status = taraStatusFromDbPapers(ids, dbPapers);
      if (status === 'none') {
        if (s1Done && s2Done) status = 'done';
        else if (s1Done || s2Done) status = 'progress';
        else if (sitting.completed) status = 'done';
      }
      const score = s1Done
        ? taraScoreFromRaw(s1.correct || 0, s1.total || 0)
        : null;
      return {
        ...sitting,
        index,
        scored: {
          s1: s1Done ? s1 : null,
          s2: s2Done ? s2 : null,
          score,
          mode: s1?.mode || s2?.mode || null,
        },
        status,
      };
    });
}

function enrichBmatSittings(sittings, scoresMap, dbPapers = {}) {
  return (sittings || [])
    .filter((s) => s?.kind === 'sitting' && s.bmatSitting)
    .map((sitting, index) => {
      const modules = sitting.bmatModules || {};
      const ids = BMAT_MODULE_KEYS.map((key) => modules[key]?.id).filter(Boolean);
      const ts = modules.thinking_skills?.id ? scoresMap[String(modules.thinking_skills.id)] : null;
      const sk = modules.scientific_knowledge?.id ? scoresMap[String(modules.scientific_knowledge.id)] : null;
      const wr = modules.writing?.id ? scoresMap[String(modules.writing.id)] : null;
      const tsDone = Boolean(ts && ts.total > 0);
      const skDone = Boolean(sk && sk.total > 0);
      const wrDone = Boolean(wr && (wr.total > 0 || wr.dbStatus === 'completed'));
      let correct = 0;
      let total = 0;
      if (tsDone) {
        correct += ts.correct || 0;
        total += ts.total || 0;
      }
      if (skDone) {
        correct += sk.correct || 0;
        total += sk.total || 0;
      }
      const score = total > 0 ? taraScoreFromRaw(correct, total) : null;
      let status = taraStatusFromDbPapers(ids, dbPapers);
      const presentDone = BMAT_MODULE_KEYS.filter((key) => modules[key]?.id).every((key) => {
        if (key === 'writing') return wrDone;
        if (key === 'thinking_skills') return tsDone;
        if (key === 'scientific_knowledge') return skDone;
        return false;
      });
      if (presentDone && ids.length) status = 'done';
      else if (status === 'none') {
        const any = tsDone || skDone || wrDone;
        status = any ? 'progress' : (sitting.completed ? 'done' : 'none');
      }
      return {
        ...sitting,
        index,
        scored: {
          thinking_skills: tsDone ? ts : null,
          scientific_knowledge: skDone ? sk : null,
          writing: wrDone ? wr : null,
          score,
          mode: ts?.mode || sk?.mode || wr?.mode || null,
        },
        status,
      };
    });
}

function StatusPill({ status }) {
  const map = {
    done: ['completed', 'Completed'],
    progress: ['in-progress', 'In progress'],
    none: ['not-started', 'Not started'],
  };
  const [cls, label] = map[status] || map.none;
  return <span className={`tara-ws-pill ${cls}`}>{label}</span>;
}

function ModeCell({ mode, status }) {
  if (status === 'none' || !mode) return '—';
  return <span className="tara-ws-mode">{mode === 'untimed' ? 'Untimed' : 'Timed'}</span>;
}

function avgScores(scores) {
  const nums = scores.filter((s) => s != null && Number.isFinite(Number(s))).map(Number);
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

function enrichSittings(sittings, scoresMap, dbPapers = {}, writingAvailability = {}) {
  return (sittings || []).map((sitting, index) => {
    const ids = sittingPaperIds(sitting);
    const scored = sittingScoreFromTaraModules(sitting.taraModules, scoresMap);
    let status = taraSittingHubStatus({
      paperIds: ids,
      dbPapers,
      scored,
      writingPaperId: sitting.taraModules?.writing?.id,
      writingAvailability,
    });
    if (status === 'none' && sitting.completed) status = 'done';
    if (scored.status === 'done') status = 'done';
    const modeFromDb = ids.map((id) => dbPapers[String(id)]?.attempt_mode).find(Boolean) || null;
    return {
      ...sitting,
      index,
      scored: { ...scored, mode: scored.mode || modeFromDb },
      status,
    };
  });
}

function tierStats(rows) {
  const done = rows.filter((r) => r.status === 'done').length;
  const scores = rows.map((r) => r.scored.score);
  return {
    done,
    total: rows.length,
    avg: avgScores(scores),
    pct: rows.length ? Math.round((done / rows.length) * 100) : 0,
  };
}

// A TARA module is scored as soon as it is answered, so in-progress scores
// are cached too.
const hasAnsweredScore = (row) => (Number(row?.score_total) || Number(row?.score_answered) || 0) > 0;

const TaraWorkspace = ({
  onBack,
  mockSittings = [],
  pastSittings = [],
  sectionPapers = [],
  subtopicPapers = [],
  topicSections = [],
  isPartUnlocked,
  onStartSetPaper,
  onStartTopicPaper,
  topicStatus = 'ready',
  onContinueSitting,
  onReviewSitting,
  onRequestUpgrade,
}) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [page, setPage] = useState('overview');
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scoreTick, setScoreTick] = useState(0);
  const [writingAvailability, setWritingAvailability] = useState({});
  const [toast, setToast] = useState(null);
  const [target, setTarget] = useUserTarget(loadTaraTarget, saveTaraTarget, userId);

  useEffect(() => {
    document.body.classList.add('exam-workspace-open');
    return () => document.body.classList.remove('exam-workspace-open');
  }, []);

  const taraStatusIds = (() => {
    const ids = [];
    [...mockSittings, ...pastSittings].forEach((s) => {
      if (s?.kind === 'sitting' && s.tsaSitting) {
        [s.paper1?.id, s.paper2?.id].filter(Boolean).forEach((id) => ids.push(id));
        return;
      }
      if (s?.kind === 'sitting' && s.bmatSitting) {
        BMAT_MODULE_KEYS.forEach((key) => {
          const id = s.bmatModules?.[key]?.id;
          if (id) ids.push(id);
        });
        return;
      }
      if (s?.kind === 'sitting') {
        TARA_MODULE_KEYS.forEach((key) => {
          const id = s.taraModules?.[key]?.id;
          if (id) ids.push(id);
        });
        return;
      }
      if (s?.id) ids.push(s.id);
    });
    ids.push(...collectTopicPaperIds(topicSections));
    return ids;
  })();
  const statusRequestKey = topicAttemptStatusRequestKey(taraStatusIds);

  const {
    dbPapers,
    statusState,
    scoresMap,
    completedList,
  } = useWorkspacePaperProgress('TARA', statusRequestKey, {
    userId,
    loadScores: loadTaraPaperScores,
    saveScore: saveTaraPaperScore,
    shouldCache: hasAnsweredScore,
    cacheTick: scoreTick,
  });


  // Which papers have writing feedback, asked about papers rather than about
  // the sitting, which may not be the one holding the essay. Asked once the
  // statuses have loaded; failing here does not disturb them, so the cell
  // falls back to the sitting's own score.
  useEffect(() => {
    let cancelled = false;
    if (statusState !== 'ready') {
      setWritingAvailability({});
      return undefined;
    }
    (async () => {
      try {
        const avail = await quizGeneratorAPI.getTaraWritingFeedbackAvailability(statusRequestKey.split('\n'));
        if (!cancelled && avail?.success) setWritingAvailability(avail.papers || {});
      } catch (availErr) {
        console.warn('TARA writing feedback availability fetch failed', availErr);
      }
    })();
    return () => { cancelled = true; };
  }, [statusState, statusRequestKey]);


  const mockSittingsOnly = useMemo(
    () => mockSittings.filter((s) => s.kind === 'sitting'),
    [mockSittings]
  );
  const pastSittingsOnly = useMemo(
    () => pastSittings.filter((s) => s.kind === 'sitting'),
    [pastSittings]
  );

  const beginnings = useMemo(
    () => enrichSittings(
      mockSittingsOnly.filter((s) => s.tier === 'beginnings'),
      scoresMap,
      dbPapers,
      writingAvailability
    ),
    [mockSittingsOnly, scoresMap, dbPapers, writingAvailability]
  );
  const foundations = useMemo(
    () => enrichSittings(
      mockSittingsOnly.filter((s) => s.tier === 'foundations'),
      scoresMap,
      dbPapers,
      writingAvailability
    ),
    [mockSittingsOnly, scoresMap, dbPapers, writingAvailability]
  );
  const beyond = useMemo(
    () => enrichSittings(
      mockSittingsOnly.filter((s) => s.tier === 'beyond'),
      scoresMap,
      dbPapers,
      writingAvailability
    ),
    [mockSittingsOnly, scoresMap, dbPapers, writingAvailability]
  );

  const mocksGoto = useMemo(
    () => firstPopulatedMockTier(beginnings, foundations, beyond),
    [beginnings, foundations, beyond]
  );
  // BMAT and TSA sittings are their sets (catalogueSittings). A BMAT part
  // whose set is not a sitting (a part with no place in the layout) is listed
  // on its own, under the BMAT sittings.
  const pastBmatPaired = useMemo(
    () => pastSittings.filter((s) => s.bmatSitting || (s.kind !== 'sitting' && paperHeritagePrefix(s) === 'BMAT')),
    [pastSittings]
  );
  const pastBmat = useMemo(
    () => enrichBmatSittings(pastBmatPaired, scoresMap, dbPapers),
    [pastBmatPaired, scoresMap, dbPapers]
  );
  const pastBmatExtras = useMemo(
    () => enrichCreamPapers(
      pastBmatPaired.filter((row) => row.kind !== 'sitting'),
      scoresMap,
      dbPapers
    ),
    [pastBmatPaired, scoresMap, dbPapers]
  );
  const pastTsa = useMemo(
    () => enrichTsaSittings(
      pastSittings.filter((s) => s.tsaSitting),
      scoresMap,
      dbPapers
    ),
    [pastSittings, scoresMap, dbPapers]
  );
  const pastTaraSittings = useMemo(
    () => enrichSittings(
      pastSittingsOnly.filter((s) => (
        !s.tsaSitting
        && paperHeritagePrefix(s) === 'TARA'
        && isTaraSpecimenOrSample(s)
      )),
      scoresMap,
      dbPapers,
      writingAvailability
    ),
    [pastSittingsOnly, scoresMap, dbPapers, writingAvailability]
  );
  const past = useMemo(
    () => [...pastBmat, ...pastBmatExtras, ...pastTsa, ...pastTaraSittings],
    [pastBmat, pastBmatExtras, pastTsa, pastTaraSittings]
  );
  const topicCount = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce((n, t) => n + (t.params?.length || 0), 0),
      0
    ),
    [topicSections]
  );
  const topicsDone = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce(
        (n, t) => n + (t.params || []).filter((p) => completedList.includes(p.paper_id)).length,
        0
      ),
      0
    ),
    [topicSections, completedList]
  );

  // Section papers are listed under Practice Topics (Foundations and Beyond), so the
  // Overview's Practice Topics rows count them with the topics.
  const sectionProgress = useSectionPaperTierProgress('TARA', sectionPapers, completedList);
  const practiceTopicsRow = practiceProgressSummary({ topicsDone, topicCount, papers: sectionProgress.all });

  const overview = useMemo(() => {
    const allMocks = [...beginnings, ...foundations, ...beyond];
    const gradedMocks = allMocks.filter((r) => r.scored.score != null);
    const gradedPastTara = pastTaraSittings.filter((r) => r.scored.score != null);
    const gradedPastBmat = pastBmat.filter((r) => r.scored.score != null);
    const gradedPastTsa = pastTsa.filter((r) => r.scored.score != null);
    const graded = [...gradedMocks, ...gradedPastTara, ...gradedPastBmat, ...gradedPastTsa];
    const currentAvg = avgScores(graded.map((r) => r.scored.score));

    let totalQuestions = 0;
    let attempted = 0;
    let correct = 0;
    [...allMocks, ...pastTaraSittings].forEach((row) => {
      totalQuestions += row.questionCount || 0;
      [row.scored.ct, row.scored.ps].forEach((s) => {
        if (!s) return;
        const answered = Number(s.answered);
        attempted += Number.isFinite(answered) ? answered : (Number(s.total) || 0);
        correct += Number(s.correct) || 0;
      });
    });
    pastBmat.forEach((row) => {
      totalQuestions += Number(row.questionCount) || 0;
      [row.scored?.thinking_skills, row.scored?.scientific_knowledge].forEach((s) => {
        if (!s) return;
        attempted += Number(s.total) || 0;
        correct += Number(s.correct) || 0;
      });
    });
    pastBmatExtras.forEach((row) => {
      totalQuestions += Number(row.question_count || row.questionCount) || 0;
      if (row.scored?.total > 0) {
        attempted += Number(row.scored.total) || 0;
        correct += Number(row.scored.correct) || 0;
      }
    });
    pastTsa.forEach((row) => {
      totalQuestions += Number(row.questionCount) || 0;
      [row.scored.s1, row.scored.s2].forEach((s) => {
        if (!s) return;
        attempted += Number(s.total) || 0;
        correct += Number(s.correct) || 0;
      });
    });

    const timed = graded.filter((r) => r.scored.mode === 'timed');
    const untimed = graded.filter((r) => r.scored.mode === 'untimed');
    const mocksDone = allMocks.filter((r) => r.status === 'done').length;
    const pastDone = past.filter((r) => r.status === 'done').length;

    return {
      currentAvg,
      totalQuestions,
      attempted,
      successRate: attempted > 0 ? Math.round((correct / attempted) * 100) : null,
      scoredAttempts: graded.length,
      beginnings: tierStats(beginnings),
      foundations: tierStats(foundations),
      beyond: tierStats(beyond),
      bmat: tierStats(pastBmat.map((r) => ({ ...r, scored: { score: r.scored.score } }))),
      tsa: tierStats(pastTsa.map((r) => ({ ...r, scored: { score: r.scored.score } }))),
      taraPast: tierStats(pastTaraSittings),
      mocks: {
        done: mocksDone,
        total: allMocks.length,
        avg: avgScores(allMocks.map((r) => r.scored.score)),
        pct: allMocks.length ? Math.round((mocksDone / allMocks.length) * 100) : 0,
      },
      past: {
        done: pastDone,
        total: past.length,
        avg: avgScores(past.map((r) => r.scored.score)),
        pct: past.length ? Math.round((pastDone / past.length) * 100) : 0,
      },
      timed: {
        count: timed.length,
        total: graded.length,
        avg: avgScores(timed.map((r) => r.scored.score)),
        pct: graded.length ? Math.round((timed.length / graded.length) * 100) : 0,
      },
      untimed: {
        count: untimed.length,
        total: graded.length,
        avg: avgScores(untimed.map((r) => r.scored.score)),
        pct: graded.length ? Math.round((untimed.length / graded.length) * 100) : 0,
      },
    };
  }, [beginnings, foundations, beyond, past, pastBmat, pastBmatExtras, pastTsa, pastTaraSittings]);

  // The writing paper is module 3 of a paired TARA sitting, so its own attempt
  // is the one the feedback hangs off, not the sitting's first paper.
  const openWritingFeedback = (sitting) => {
    const writingPaperId = sitting?.taraModules?.writing?.id;
    const access = taraWritingFeedbackAccess({
      writingPaperId,
      dbPapers,
      availability: writingAvailability,
      scoredWriting: sitting?.scored?.writing,
    });
    if (!access.available) {
      showToast('That writing task has no saved attempt to look at yet.');
      return;
    }
    const writingPaper = sitting?.taraModules?.writing;
    setFeedbackFor({
      attemptId: access.attemptId,
      paperId: writingPaperId,
      paper: writingPaper,
      // Feedback follows the writing paper's own lock: open when that paper is.
      // No paper is refused by the server, so it shows locked too.
      unlocked: Boolean(writingPaper) && (isPartUnlocked ? isPartUnlocked(writingPaper) : true),
      fromEarlierSitting: access.fromEarlierSitting,
      label: sitting?.label || sitting?.title || 'Writing Task',
    });
    setPage('writing-feedback');
  };

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 2600);
  };

  const goTo = (id) => {
    setPage(id);
    setMobileOpen(false);
  };

  const startSitting = (sitting, options = {}) => {
    if (sitting?.tsaSitting) {
      const paper = sitting.paper1 || sitting.paper2;
      if (!paper) return;
      if (isPartUnlocked && !isPartUnlocked(paper)) {
        onRequestUpgrade?.(paper);
        return;
      }
      onStartSetPaper(
        'TARA',
        sitting.startId,
        sitting.startCatalogId,
        sitting.paper2
          ? {
            id: sitting.paper2.id,
            catalog_id: sitting.paper2.catalog_id || sitting.paper2.id,
            tsaSitting: true,
            pearsonSitting: true,
          }
          : { tsaSitting: true, pearsonSitting: true },
        options
      );
      return;
    }
    if (sitting?.bmatSitting) {
      const modules = sitting.bmatModules || {};
      const first = BMAT_MODULE_KEYS.map((k) => modules[k]).find(Boolean) || sitting.paper1;
      if (!first) return;
      if (isPartUnlocked && !isPartUnlocked(first)) {
        onRequestUpgrade?.(first);
        return;
      }
      onStartSetPaper('TARA', sitting.startId, sitting.startCatalogId, {
        pearsonSitting: true,
        bmatSitting: true,
        heritageExam: 'BMAT',
        bmatModules: sitting.bmatModules,
      }, options);
      return;
    }
    const paper = sitting.taraModules?.critical_thinking || sitting.paper1;
    if (!paper) return;
    if (isPartUnlocked && !isPartUnlocked(paper)) {
      onRequestUpgrade?.(paper);
      return;
    }
    onStartSetPaper(
      'TARA',
      sitting.startId,
      sitting.startCatalogId,
      { pearsonSitting: true, taraModules: sitting.taraModules },
      options
    );
  };

  const startCreamPaper = (paper, options = {}) => {
    if (!paper?.id) return;
    if (isPartUnlocked && !isPartUnlocked(paper)) {
      onRequestUpgrade?.(paper);
      return;
    }
    onStartSetPaper('TARA', paper.id, paper.catalog_id || paper.id, null, options);
  };

  const handleRetake = (sitting) => {
    resetTaraSittingProgress(userId, sittingPaperIds(sitting));
    setScoreTick((n) => n + 1);
    startSitting(sitting, { forceNew: true });
  };

  const handleRetakeCream = (paper) => {
    resetTaraSittingProgress(userId, [paper.id]);
    setScoreTick((n) => n + 1);
    startCreamPaper(paper, { forceNew: true });
  };

  const handleContinue = (sitting) => {
    if (typeof onContinueSitting === 'function' && sitting) {
      onContinueSitting(sitting);
      return;
    }
    showToast('Resume isn’t available yet — use Start or Retake for a new attempt.');
  };

  const handleReview = (sitting) => {
    if (typeof onReviewSitting === 'function' && sitting) {
      onReviewSitting(sitting);
      return;
    }
    showToast('Review isn’t available yet.');
  };

  const targetValue = Number(target?.value) || 0;
  const avg = overview.currentAvg;
  const fillPct = avg != null ? Math.min(100, avg) : 0;
  const markerPct = Math.min(100, targetValue);
  const diff = avg != null ? Math.round((avg - targetValue) * 10) / 10 : null;
  const markerLabel = (target?.label || 'Target').split(' — ')[0] || 'Target';

  const selectedOption = TARGET_OPTIONS.find((o) => o.label === target?.label);
  const selectValue = target?.custom ? 'custom' : (selectedOption?.label || TARGET_OPTIONS[2].label);
  const selectedTrack = TARGET_OPTIONS.find((o) => o.label === selectValue);
  const selectDisplay = selectValue === 'custom'
    ? 'Custom target…'
    : selectedTrack
      ? `${selectedTrack.label} (${selectedTrack.value}+/100)`
      : selectValue;

  const onTargetChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      const next = { value: Number(target.value) || 64, label: 'Custom', custom: true };
      setTarget(next);
      return;
    }
    const opt = TARGET_OPTIONS.find((o) => o.label === val);
    if (!opt) return;
    const next = { value: opt.value, label: opt.label, requirement: opt.requirement, custom: false };
    setTarget(next);
  };

  const onCustomInput = (e) => {
    const raw = Number(e.target.value) || 0;
    const value = Math.max(0, Math.min(100, raw));
    const next = { value, label: 'Custom', custom: true };
    setTarget(next);
  };

  const renderTopicsTable = (params) => {
    if (!params?.length) return null;
    return (
      <table className="tara-ws-table">
        <thead>
          <tr>
            <th>Topic</th>
            <th>Questions</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {params.map((param) => {
            const done = completedList.includes(param.paper_id);
            const action = topicRowAction(topicAttemptRowFromMap(dbPapers, param.paper_id), done);
            const status = action.status;
            const title = param.title || param.paper_id;
            return (
              <tr key={param.paper_id}>
                <td data-label="Topic" className="tara-ws-cell-strong">{title}</td>
                <td data-label="Questions" className="tara-ws-cell-mono">
                  {param.question_count != null ? param.question_count : '—'}
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  <button
                    type="button"
                    className={action.resume ? 'tara-ws-start-btn mid' : 'tara-ws-start-btn'}
                    onClick={() => onStartTopicPaper?.('TARA', param, {
                      forceNew: action.forceNew,
                      resume: action.resume,
                    })}
                  >
                    {action.label}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderTable = (rows, kind) => {
    if (!rows.length) {
      return (
        <div className="tara-ws-empty">
          <p>No past papers in this set yet.</p>
        </div>
      );
    }
    return (
    <table className="tara-ws-table">
      <thead>
        <tr>
          <th>{kind === 'past' ? 'Paper' : 'Test'}</th>
          <th>Critical Thinking</th>
          <th>Problem Solving</th>
          <th>Writing Task</th>
          <th>Score</th>
          <th>Mode</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const paper = row.taraModules?.critical_thinking;
          const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
          const { scored, status } = row;
          return (
            <tr key={row.startId || row.title}>
              <td data-label={kind === 'past' ? 'Paper' : 'Test'} className="tara-ws-cell-strong">
                {sittingLabel(row, row.index, kind)}
                {!unlocked && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--tara-ink-faint)' }}>Locked</span>
                )}
              </td>
              <td data-label="Critical Thinking" className="tara-ws-cell-mono">
                {formatPaperRaw(scored.ct?.correct, scored.ct?.total)}
              </td>
              <td data-label="Problem Solving" className="tara-ws-cell-mono">
                {formatPaperRaw(scored.ps?.correct, scored.ps?.total)}
              </td>
              <td data-label="Writing Task" className="tara-ws-cell-mono">
                {taraWritingFeedbackAccess({
                  writingPaperId: row?.taraModules?.writing?.id,
                  dbPapers,
                  availability: writingAvailability,
                  scoredWriting: scored.writing,
                }).available ? (
                  <button
                    type="button"
                    className="tara-ws-start-btn ghost"
                    onClick={() => openWritingFeedback(row)}
                  >
                    Submitted &middot; View feedback
                  </button>
                ) : '—'}
              </td>
              <td data-label="Score" className="tara-ws-cell-mono">
                {formatTaraScore(scored.score)}
              </td>
              <td data-label="Mode">
                <ModeCell mode={scored.mode} status={status} />
              </td>
              <td data-label="Status">
                <StatusPill status={status} />
              </td>
              <td data-label="">
                {status === 'done' ? (
                  <div className="tara-ws-actions">
                    <button type="button" className="tara-ws-start-btn ghost" onClick={() => handleReview(row)}>
                      Review
                    </button>
                    <button
                      type="button"
                      className="tara-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : status === 'progress' ? (
                  <div className="tara-ws-actions">
                    <button type="button" className="tara-ws-start-btn mid" onClick={() => handleContinue(row)}>
                      Continue
                    </button>
                    <button
                      type="button"
                      className="tara-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="tara-ws-start-btn"
                    onClick={() => {
                      if (!unlocked) {
                        onRequestUpgrade?.(paper);
                        return;
                      }
                      startSitting(row);
                    }}
                  >
                    Start
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    );
  };

  const renderTsaTable = (rows) => {
    if (!rows.length) {
      return (
        <div className="tara-ws-empty">
          <p>No past papers in this set yet.</p>
        </div>
      );
    }
    return (
      <table className="tara-ws-table">
        <thead>
          <tr>
            <th>Paper</th>
            <th>Section 1</th>
            <th>Section 2</th>
            <th>Score</th>
            <th>Mode</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const paper = row.paper1 || row.paper2;
            const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
            const { scored, status } = row;
            return (
              <tr key={row.startId || row.title}>
                <td data-label="Paper" className="tara-ws-cell-strong">
                  {sittingLabel(row, row.index, 'past')}
                  {!unlocked && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--tara-ink-faint)' }}>Locked</span>
                  )}
                </td>
                <td data-label="Section 1" className="tara-ws-cell-mono">
                  {formatPaperRaw(scored.s1?.correct, scored.s1?.total)}
                </td>
                <td data-label="Section 2" className="tara-ws-cell-mono">
                  {scored.s2 ? (scored.s2.total > 0 ? formatPaperRaw(scored.s2.correct, scored.s2.total) : 'Submitted') : '—'}
                </td>
                <td data-label="Score" className="tara-ws-cell-mono">
                  {formatTaraScore(scored.score)}
                </td>
                <td data-label="Mode">
                  <ModeCell mode={scored.mode} status={status} />
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  {status === 'done' ? (
                    <div className="tara-ws-actions">
                      <button type="button" className="tara-ws-start-btn ghost" onClick={() => handleReview(row)}>
                        Review
                      </button>
                      <button
                        type="button"
                        className="tara-ws-start-btn ghost"
                        onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                      >
                        Retake
                      </button>
                    </div>
                  ) : status === 'progress' ? (
                    <div className="tara-ws-actions">
                      <button type="button" className="tara-ws-start-btn mid" onClick={() => handleContinue(row)}>
                        Continue
                      </button>
                      <button
                        type="button"
                        className="tara-ws-start-btn ghost"
                        onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                      >
                        Retake
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="tara-ws-start-btn"
                      onClick={() => {
                        if (!unlocked) {
                          onRequestUpgrade?.(paper);
                          return;
                        }
                        startSitting(row);
                      }}
                    >
                      Start
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderBmatTable = (rows) => {
    if (!rows.length) {
      return (
        <div className="tara-ws-empty">
          <p>No past papers in this set yet.</p>
        </div>
      );
    }
    return (
      <table className="tara-ws-table">
        <thead>
          <tr>
            <th>Paper</th>
            <th>{BMAT_MODULE_LABELS.thinking_skills}</th>
            <th>{BMAT_MODULE_LABELS.scientific_knowledge}</th>
            <th>{BMAT_MODULE_LABELS.writing}</th>
            <th>Score</th>
            <th>Mode</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const modules = row.bmatModules || {};
            const paper = BMAT_MODULE_KEYS.map((k) => modules[k]).find(Boolean) || row.paper1;
            const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
            const { scored, status } = row;
            const thinking = modules.thinking_skills;
            const scientific = modules.scientific_knowledge;
            const writing = modules.writing;
            return (
              <tr key={row.startId || row.title}>
                <td data-label="Paper" className="tara-ws-cell-strong">
                  {sittingLabel(row, row.index, 'past')}
                  {!unlocked && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--tara-ink-faint)' }}>Locked</span>
                  )}
                </td>
                <td data-label={BMAT_MODULE_LABELS.thinking_skills} className="tara-ws-cell-mono">
                  {thinking ? formatPaperRaw(scored.thinking_skills?.correct, scored.thinking_skills?.total) : '—'}
                </td>
                <td data-label={BMAT_MODULE_LABELS.scientific_knowledge} className="tara-ws-cell-mono">
                  {scientific ? formatPaperRaw(scored.scientific_knowledge?.correct, scored.scientific_knowledge?.total) : '—'}
                </td>
                <td data-label={BMAT_MODULE_LABELS.writing} className="tara-ws-cell-mono">
                  {writing ? (scored.writing ? 'Submitted' : '—') : '—'}
                </td>
                <td data-label="Score" className="tara-ws-cell-mono">
                  {formatTaraScore(scored.score)}
                </td>
                <td data-label="Mode">
                  <ModeCell mode={scored.mode} status={status} />
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  {status === 'done' ? (
                    <div className="tara-ws-actions">
                      <button type="button" className="tara-ws-start-btn ghost" onClick={() => handleReview(row)}>
                        Review
                      </button>
                      <button
                        type="button"
                        className="tara-ws-start-btn ghost"
                        onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                      >
                        Retake
                      </button>
                    </div>
                  ) : status === 'progress' ? (
                    <div className="tara-ws-actions">
                      <button type="button" className="tara-ws-start-btn mid" onClick={() => handleContinue(row)}>
                        Continue
                      </button>
                      <button
                        type="button"
                        className="tara-ws-start-btn ghost"
                        onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                      >
                        Retake
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="tara-ws-start-btn"
                      onClick={() => {
                        if (!unlocked) {
                          onRequestUpgrade?.(paper);
                          return;
                        }
                        startSitting(row);
                      }}
                    >
                      Start
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderCreamTable = (rows) => {
    if (!rows.length) {
      return (
        <div className="tara-ws-empty">
          <p>No past papers in this set yet.</p>
        </div>
      );
    }
    return (
      <table className="tara-ws-table">
        <thead>
          <tr>
            <th>Paper</th>
            <th>Questions</th>
            <th>Score</th>
            <th>Mode</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const unlocked = isPartUnlocked ? isPartUnlocked(row) : true;
            const { scored, status } = row;
            return (
              <tr key={row.id || row.catalog_id}>
                <td data-label="Paper" className="tara-ws-cell-strong">
                  {creamPaperLabel(row)}
                  {!unlocked && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--tara-ink-faint)' }}>Locked</span>
                  )}
                </td>
                <td data-label="Questions" className="tara-ws-cell-mono">
                  {row.question_count || row.questionCount || '—'}
                </td>
                <td data-label="Score" className="tara-ws-cell-mono">
                  {scored.total > 0
                    ? `${formatPaperRaw(scored.correct, scored.total)} · ${formatTaraScore(scored.score)}`
                    : '—'}
                </td>
                <td data-label="Mode">
                  <ModeCell mode={scored.mode} status={status} />
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  {status === 'done' ? (
                    <div className="tara-ws-actions">
                      <button type="button" className="tara-ws-start-btn ghost" onClick={() => handleReview(row)}>
                        Review
                      </button>
                      <button
                        type="button"
                        className="tara-ws-start-btn ghost"
                        onClick={() => (unlocked ? handleRetakeCream(row) : onRequestUpgrade?.(row))}
                      >
                        Retake
                      </button>
                    </div>
                  ) : status === 'progress' ? (
                    <div className="tara-ws-actions">
                      <button
                        type="button"
                        className="tara-ws-start-btn mid"
                        onClick={() => (unlocked ? startCreamPaper(row) : onRequestUpgrade?.(row))}
                      >
                        Continue
                      </button>
                      <button
                        type="button"
                        className="tara-ws-start-btn ghost"
                        onClick={() => (unlocked ? handleRetakeCream(row) : onRequestUpgrade?.(row))}
                      >
                        Retake
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="tara-ws-start-btn"
                      onClick={() => (unlocked ? startCreamPaper(row) : onRequestUpgrade?.(row))}
                    >
                      Start
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const BreakdownRow = ({ name, meta, score, pct, goto, staticRow }) => {
    const inner = (
      <>
        <div className="tara-ws-breakdown-name">{name}</div>
        <div className="tara-ws-breakdown-bar-wrap">
          <div className="tara-ws-breakdown-bar">
            <div className="tara-ws-breakdown-bar-fill" style={{ width: `${pct || 0}%` }} />
          </div>
          <div className="tara-ws-breakdown-meta">{meta}</div>
        </div>
        <div className="tara-ws-breakdown-score">{score}</div>
        {!staticRow && <div className="tara-ws-breakdown-arrow">›</div>}
      </>
    );
    if (staticRow) {
      return <div className="tara-ws-breakdown-row static">{inner}</div>;
    }
    return (
      <button type="button" className="tara-ws-breakdown-row" onClick={() => goTo(goto)}>
        {inner}
      </button>
    );
  };

  return (
    <div className="tara-ws">
      <aside className={`tara-ws-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="tara-ws-sidebar-head">
          <span className="tara-ws-name">TARA Workspace</span>
          <button
            type="button"
            className="tara-ws-icon-btn"
            title="Collapse sidebar"
            onClick={() => (mobileOpen ? setMobileOpen(false) : setCollapsed((c) => !c))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
              <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <nav className="tara-ws-nav">
          <button
            type="button"
            className={`tara-ws-nav-item ${page === 'overview' ? 'active' : ''}`}
            onClick={() => goTo('overview')}
          >
            <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="tara-ws-nav-label">Overview</span>
          </button>

          <PracticeTopicsNav prefix="tara-ws" page={page} onGo={goTo} />
          <PracticeTopicsNav
            prefix="tara-ws"
            page={page}
            onGo={goTo}
            label={SUBTOPIC_PAPERS_LABEL}
            pages={SUBTOPIC_TIER_PAGES}
          />

          <button
            type="button"
            className={`tara-ws-nav-item ${page === 'writing-feedback' ? 'active' : ''}`}
            onClick={() => goTo('writing-feedback')}
          >
            <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <path d="M5 19.5V5.5A1.5 1.5 0 016.5 4H15l4 4v11.5a.5.5 0 01-.5.5h-13a.5.5 0 01-.5-.5z" stroke="currentColor" strokeWidth="1.6" />
              <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.6" />
              <line x1="9" y1="16" x2="13" y2="16" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="tara-ws-nav-label">Writing Feedback</span>
          </button>

          <div className="tara-ws-nav-group">
            <div className="tara-ws-nav-item tara-ws-nav-parent">
              <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <span className="tara-ws-nav-label">Mock Tests</span>
            </div>
            <div className="tara-ws-nav-sub">
              {[
                ['mock-beginnings', 'Beginnings', 1],
                ['mock-foundations', 'Foundations', 2],
                ['mock-beyond', 'Beyond', 3],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`tara-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="tara-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {[
            ['mock-beginnings', 'Beginnings', 1],
            ['mock-foundations', 'Foundations', 2],
            ['mock-beyond', 'Beyond', 3],
            ['past-tara', 'TARA', 1],
            ['past-bmat', 'BMAT', 2],
            ['past-tsa', 'TSA', 3],
          ].map(([id, label, tier]) => (
            <button
              key={`c-${id}`}
              type="button"
              className={`tara-ws-nav-item tara-ws-collapsed-tier ${page === id ? 'active' : ''}`}
              title={label}
              onClick={() => goTo(id)}
            >
              <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
              </svg>
              <span className="tara-ws-nav-label">{label}</span>
            </button>
          ))}

          <div className="tara-ws-nav-group">
            <div className="tara-ws-nav-item tara-ws-nav-parent">
              <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M4 19.5V6a2 2 0 012-2h10.5a1.5 1.5 0 011.5 1.5V19a1 1 0 01-1 1H6.5A2.5 2.5 0 014 19.5z" stroke="currentColor" strokeWidth="1.6" />
                <line x1="8" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.6" />
              </svg>
              <span className="tara-ws-nav-label">Past Papers</span>
            </div>
            <div className="tara-ws-nav-sub">
              {[
                ['past-tara', 'TARA', 1],
                ['past-bmat', 'BMAT', 2],
                ['past-tsa', 'TSA', 3],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`tara-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="tara-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="tara-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>
      </aside>

      <main className="tara-ws-main">
        <div className="tara-ws-topbar-mobile">
          <button type="button" className="tara-ws-icon-btn" onClick={() => setMobileOpen((o) => !o)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <span>{PAGES[page]}</span>
        </div>

        <div className="tara-ws-back-row">
          <button type="button" className="tara-ws-icon-btn tara-ws-back" title="Back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {page === 'overview' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Overview</h1>
            </div>

            <div className="tara-ws-target-card">
              <div className="tara-ws-target-field tara-ws-target-field--university">
                <label htmlFor="tara-track">Target university</label>
                <div className="tara-ws-target-select-shell">
                  <span className="tara-ws-target-select-text" aria-hidden="true">
                    {selectDisplay}
                  </span>
                  <select
                    id="tara-track"
                    className="tara-ws-target-select"
                    value={selectValue}
                    onChange={onTargetChange}
                  >
                    {TARGET_OPTIONS.map((o) => (
                      <option key={o.label} value={o.label}>
                        {o.label} ({o.value}+/100)
                      </option>
                    ))}
                    <option value="custom">Custom target…</option>
                  </select>
                </div>
              </div>
              <div className="tara-ws-target-field">
                <label htmlFor="tara-custom">Custom score</label>
                <input
                  id="tara-custom"
                  type="number"
                  className="tara-ws-target-input"
                  min={0}
                  max={100}
                  step={1}
                  placeholder="/ 100"
                  hidden={!target.custom}
                  value={target.custom ? target.value : ''}
                  onChange={onCustomInput}
                />
              </div>
              <div className="tara-ws-target-readout">
                <span className="num">
                  {targetValue}
                  <span style={{ color: 'var(--tara-ink-faint)', fontWeight: 500 }}>/100</span>
                </span>
                <div className="lbl">TARA score target</div>
                {!target.custom && (
                  <div style={{ fontSize: 11, color: 'var(--tara-ink-faint)', marginTop: 2 }}>
                    {(selectedOption?.requirement || target.requirement)} for this course
                  </div>
                )}
              </div>
            </div>

            <div className="tara-ws-section">
              <div className="tara-ws-progress-status" style={{ marginBottom: 8 }}>
                {avg == null ? (
                  'No scored sittings yet — complete a mock or past paper to see progress.'
                ) : diff >= 0 ? (
                  <>
                    <b>{diff.toFixed(1)} points</b> above target — keep it up
                  </>
                ) : (
                  <>
                    <b>{Math.abs(diff).toFixed(1)} points</b> below target
                  </>
                )}
              </div>
              <div className="tara-ws-progress-track">
                <div className="tara-ws-progress-fill" style={{ width: `${fillPct}%` }} />
                <div
                  className="tara-ws-progress-marker"
                  data-label={markerLabel}
                  style={{ left: `${markerPct}%` }}
                />
              </div>
              <div className="tara-ws-breakdown-meta">
                Current average:{' '}
                <b style={{ color: 'var(--tara-ink)', fontFamily: 'var(--tara-mono)' }}>
                  {formatTaraScore(avg)}
                </b>
                {overview.scoredAttempts > 0
                  ? ` · based on ${overview.scoredAttempts} scored sitting${overview.scoredAttempts === 1 ? '' : 's'}`
                  : ''}
              </div>
            </div>

            <div className="tara-ws-stat-strip">
              <div className="tara-ws-stat-cell">
                <span className="tara-ws-stat-value">{overview.totalQuestions.toLocaleString()}</span>
                <span className="tara-ws-stat-label">Total questions</span>
              </div>
              <div className="tara-ws-stat-cell">
                <span className="tara-ws-stat-value">{overview.attempted.toLocaleString()}</span>
                <span className="tara-ws-stat-label">Questions attempted</span>
              </div>
              <div className="tara-ws-stat-cell">
                <span className="tara-ws-stat-value">
                  {overview.successRate != null ? `${overview.successRate}%` : '—'}
                </span>
                <span className="tara-ws-stat-label">Success rate</span>
              </div>
              <div className="tara-ws-stat-cell">
                <span className="tara-ws-stat-value">{formatTaraScore(avg)}</span>
                <span className="tara-ws-stat-label">Average score</span>
              </div>
            </div>

            <div className="tara-ws-section">
              <h2 className="tara-ws-section-title">By tier</h2>
              <BreakdownRow
                name="Beginnings"
                meta={`${overview.beginnings.done} of ${overview.beginnings.total} mocks completed`}
                score={formatTaraScore(overview.beginnings.avg)}
                pct={overview.beginnings.pct}
                goto="mock-beginnings"
              />
              <BreakdownRow
                name="Foundations"
                meta={`${overview.foundations.done} of ${overview.foundations.total} mocks completed`}
                score={formatTaraScore(overview.foundations.avg)}
                pct={overview.foundations.pct}
                goto="mock-foundations"
              />
              <BreakdownRow
                name="Beyond"
                meta={`${overview.beyond.done} of ${overview.beyond.total} mocks completed`}
                score={formatTaraScore(overview.beyond.avg)}
                pct={overview.beyond.pct}
                goto="mock-beyond"
              />
            </div>

            <div className="tara-ws-section">
              <h2 className="tara-ws-section-title">By type</h2>
              <BreakdownRow
                name="Practice Topics"
                meta={practiceTopicsRow.meta}
                score={practiceTopicsRow.score}
                pct={practiceTopicsRow.pct}
                goto="practice"
              />
              <BreakdownRow
                name="Mock Tests"
                meta={`${overview.mocks.done} of ${overview.mocks.total} mocks completed`}
                score={formatTaraScore(overview.mocks.avg)}
                pct={overview.mocks.pct}
                goto="mock-all"
              />
              <BreakdownRow
                name="TARA Past Papers"
                meta={`${overview.taraPast.done} of ${overview.taraPast.total} papers completed`}
                score={formatTaraScore(overview.taraPast.avg)}
                pct={overview.taraPast.pct}
                goto="past-tara"
              />
              <BreakdownRow
                name="BMAT Past Papers"
                meta={`${overview.bmat.done} of ${overview.bmat.total} papers completed`}
                score={formatTaraScore(overview.bmat.avg)}
                pct={overview.bmat.pct}
                goto="past-bmat"
              />
              <BreakdownRow
                name="TSA Past Papers"
                meta={`${overview.tsa.done} of ${overview.tsa.total} papers completed`}
                score={formatTaraScore(overview.tsa.avg)}
                pct={overview.tsa.pct}
                goto="past-tsa"
              />
            </div>

            <PracticeTopicsOverview
              prefix="tara-ws"
              topicsDone={topicsDone}
              topicCount={topicCount}
              papers={sectionProgress}
              onGo={goTo}
            />

            <div className="tara-ws-section">
              <h2 className="tara-ws-section-title">By mode</h2>
              <BreakdownRow
                name="Timed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.timed.count} of ${overview.timed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatTaraScore(overview.timed.avg)}
                pct={overview.timed.pct}
                staticRow
              />
              <BreakdownRow
                name="Untimed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.untimed.count} of ${overview.untimed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatTaraScore(overview.untimed.avg)}
                pct={overview.untimed.pct}
                staticRow
              />
            </div>
          </section>
        )}

        <PracticeTopicsPages
          page={page}
          prefix="tara-ws"
          exam="TARA"
          topics={(tier) => (
            topicPapersContent({
              prefix: "tara-ws",
              tier,
              topicSections,
              topicStatus,
              renderTable: renderTopicsTable,
            })
          )}
          sectionPapers={sectionPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />
        <PracticeTopicsPages
          page={page}
          prefix="tara-ws"
          exam="TARA"
          label={SUBTOPIC_PAPERS_LABEL}
          allPage={SUBTOPIC_PAPERS_ALL_PAGE}
          tierPages={SUBTOPIC_TIER_PAGES}
          titles={SUBTOPIC_PAGE_TITLES}
          ofTier={subtopicPapersOfTier}
          papers={subtopicPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />

        {page === 'writing-feedback' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h2 className="tara-ws-page-title">Writing Task Feedback</h2>
              {feedbackFor ? (
                <p className="tara-ws-page-sub">{feedbackFor.label}</p>
              ) : null}
            </div>
            {feedbackFor ? (
              <TaraWritingFeedbackPanel
                attemptId={feedbackFor.attemptId}
                onToast={showToast}
                unlocked={feedbackFor.unlocked}
                onRequestUpgrade={() => onRequestUpgrade?.(feedbackFor.paper)}
              />
            ) : (
              <div className="tara-ws-empty">
                <p>
                  Open a completed sitting from Past Papers or Mock Tests and select its
                  Writing Task to see feedback.
                </p>
              </div>
            )}
          </section>
        )}

        {page === 'mock-all' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Mock Tests</h1>
            </div>
            {[
              ['Beginnings', beginnings],
              ['Foundations', foundations],
              ['Beyond', beyond],
            ].filter(([, rows]) => rows.length).map(([label, rows]) => (
              <div key={label} className="tara-ws-section">
                <h2 className="tara-ws-section-title">{label}</h2>
                {renderTable(rows, 'mock')}
              </div>
            ))}
          </section>
        )}

        {page === 'mock-beginnings' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Mock Tests: Beginnings</h1>
            </div>
            {renderTable(beginnings, 'mock')}
          </section>
        )}

        {page === 'mock-foundations' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Mock Tests: Foundations</h1>
            </div>
            {renderTable(foundations, 'mock')}
          </section>
        )}

        {page === 'mock-beyond' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Mock Tests: Beyond</h1>
            </div>
            {renderTable(beyond, 'mock')}
          </section>
        )}

        {page === 'past-bmat' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Past Papers: BMAT</h1>
            </div>
            {renderBmatTable(pastBmat)}
            {pastBmatExtras.length ? renderCreamTable(pastBmatExtras) : null}
          </section>
        )}

        {page === 'past-tsa' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Past Papers: TSA</h1>
            </div>
            {renderTsaTable(pastTsa)}
          </section>
        )}

        {page === 'past-tara' && (
          <section className="tara-ws-page">
            <div className="tara-ws-page-header">
              <h1 className="tara-ws-page-title">Past Papers: TARA</h1>
            </div>
            {renderTable(pastTaraSittings, 'past')}
          </section>
        )}
      </main>

      <div className={`tara-ws-toast ${toast ? 'show' : ''}`}>{toast}</div>
    </div>
  );
};

export default TaraWorkspace;
